/**
 * MailViewerDialog — Iter Feb 2026 · R66.
 *
 * Opens a popup showing the full sent (or received) email tied to a
 * blended-history entry. Fetches by activityId (outbound composer sends)
 * OR replyId (inbound IMAP-captured replies). Supports both explicit
 * doc `id` and Message-ID lookup so legacy rows still open.
 *
 * User can Reply or Forward — those actions bubble up via `onReply` /
 * `onForward` so the parent can open its own MailComposer with the
 * appropriate defaults.
 *
 * Props:
 *   open          bool
 *   onOpenChange  (bool) => void
 *   activityKey   string | null  — id OR message_id of an outbound activity
 *   replyKey      string | null  — id OR message_id of an inbound reply
 *   onReply       (mail) => void — optional (falsy = hides Reply btn)
 *   onForward     (mail) => void — optional (falsy = hides Forward btn)
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Reply, Forward, Paperclip, Mail, ArrowLeftRight } from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { SmartRichTextDisplay } from "@/components/SmartRichText";

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function MailViewerDialog({
  open, onOpenChange, activityKey, replyKey, onReply, onForward,
}) {
  const [loading, setLoading] = useState(false);
  const [mail, setMail] = useState(null);

  useEffect(() => {
    if (!open) { setMail(null); return; }
    const key = activityKey || replyKey;
    if (!key) return;
    const path = activityKey ? `/mail/activity/${encodeURIComponent(activityKey)}`
                              : `/mail/reply/${encodeURIComponent(replyKey)}`;
    setLoading(true);
    api.get(path)
      .then((r) => setMail(r.data))
      .catch((e) => {
        toast.error(apiErrorMessage(e, "Could not load email"));
        onOpenChange?.(false);
      })
      .finally(() => setLoading(false));
  }, [open, activityKey, replyKey, onOpenChange]);

  const isInbound = mail?.direction === "in";
  const dateIso = mail?.date || mail?.created_at;
  const attachments = mail?.attachments || [];
  const attachmentCount = mail?.attachment_count ?? attachments.length;
  const cc = mail?.cc || [];
  const bcc = mail?.bcc || [];

  const canReply = !!onReply && !!mail;
  const canForward = !!onForward && !!mail;

  const bodyHtml = useMemo(() => mail?.body_html || mail?.html || "", [mail]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col rounded-none"
        data-testid="mail-viewer-dialog"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4" />
            <span className="truncate" data-testid="mail-viewer-subject">
              {loading ? "Loading…" : (mail?.subject || "(no subject)")}
            </span>
            {mail && (
              <span
                className={`ml-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${isInbound ? "border-emerald-500 text-emerald-700 dark:text-emerald-300" : "border-indigo-500 text-indigo-700 dark:text-indigo-300"}`}
                data-testid="mail-viewer-direction"
              >
                {isInbound ? "Received" : "Sent"}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-10 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Fetching message…
          </div>
        ) : !mail ? (
          <div className="flex-1 flex items-center justify-center py-10 text-sm text-muted-foreground">
            Message not available.
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
            {/* Meta block */}
            <div className="border p-2.5 bg-neutral-50 dark:bg-neutral-900/40 text-xs space-y-1"
                 data-testid="mail-viewer-meta">
              <div className="flex gap-2">
                <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">From</span>
                <span className="break-all">
                  {isInbound
                    ? (mail.from || "(unknown)")
                    : `${mail.from_name || mail.sent_by_name || ""} ${mail.sent_by_email ? "<" + mail.sent_by_email + ">" : ""}`}
                </span>
              </div>
              <div className="flex gap-2">
                <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">To</span>
                <span className="break-all">
                  {isInbound
                    ? (mail.original_to || "—")
                    : (mail.to_name ? `${mail.to_name} <${mail.to}>` : mail.to)}
                </span>
              </div>
              {cc.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Cc</span>
                  <span className="break-all">{cc.join(", ")}</span>
                </div>
              )}
              {bcc.length > 0 && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Bcc</span>
                  <span className="break-all">{bcc.join(", ")}</span>
                </div>
              )}
              {mail.reply_to && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Reply-to</span>
                  <span className="break-all">{mail.reply_to}</span>
                </div>
              )}
              <div className="flex gap-2">
                <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Date</span>
                <span>{_fmtDate(dateIso)}</span>
              </div>
              {mail.provider && !isInbound && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Via</span>
                  <span>{mail.provider}{mail.mailbox_id ? ` · mailbox ${mail.mailbox_id.slice(0, 6)}…` : ""}</span>
                </div>
              )}
              {mail.status && !isInbound && mail.status !== "sent" && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Status</span>
                  <span className="text-rose-700 dark:text-rose-300">{mail.status}{mail.error ? ` · ${mail.error}` : ""}</span>
                </div>
              )}
              {isInbound && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground w-14 shrink-0 uppercase text-[10px] tracking-widest pt-0.5">Matched</span>
                  <span>{mail.matched ? "Yes · linked to original send" : "No · orphan reply"}</span>
                </div>
              )}
            </div>

            {/* Attachments */}
            {attachmentCount > 0 && (
              <div className="border p-2 text-xs" data-testid="mail-viewer-attachments">
                <div className="uppercase text-[10px] tracking-widest text-muted-foreground mb-1 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> Attachments · {attachmentCount}
                </div>
                {attachments.length === 0 ? (
                  <div className="text-muted-foreground">Attachment metadata not available for this record.</div>
                ) : (
                  <ul className="space-y-0.5">
                    {attachments.map((a, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Paperclip className="w-3 h-3 text-muted-foreground" />
                        <span className="truncate">{a.filename || "attachment"}</span>
                        {a.mime && <span className="text-muted-foreground text-[10px]">· {a.mime}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Body */}
            <div className="border p-3 bg-background" data-testid="mail-viewer-body">
              {bodyHtml
                ? <SmartRichTextDisplay html={bodyHtml} testid="mail-viewer-body-html" />
                : <div className="text-xs text-muted-foreground whitespace-pre-wrap">
                    {mail.text_preview || "(empty body)"}
                  </div>}
            </div>
          </div>
        )}

        <DialogFooter className="shrink-0 gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => onOpenChange?.(false)}
            data-testid="mail-viewer-close"
          >
            Close
          </Button>
          {canForward && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-none"
              onClick={() => onForward(mail)}
              data-testid="mail-viewer-forward"
            >
              <Forward className="w-3 h-3 mr-1" /> Forward
            </Button>
          )}
          {canReply && (
            <Button
              size="sm"
              className="rounded-none"
              onClick={() => onReply(mail)}
              data-testid="mail-viewer-reply"
            >
              <Reply className="w-3 h-3 mr-1" /> Reply
            </Button>
          )}
          {!canReply && !canForward && mail && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <ArrowLeftRight className="w-3 h-3" /> Read-only view
            </span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
